import {computed, Injectable, signal} from '@angular/core';
import {Subject, Observable, defer, of, tap, finalize} from 'rxjs';
import { concatMap, catchError } from 'rxjs/operators';

export type QueueTask<T = any> = () => Observable<T>;

interface QueuedTask<T = any> {
  task: QueueTask<T>;
  subject: Subject<T | null>;
}

@Injectable({ providedIn: 'root' })
export class QueueService {
  private queue$ = new Subject<QueuedTask>();
  private pendingCount = signal(0);

  /**
   * How many tasks are queued - waiting or in flight. The one piece of save state: everything the
   * UI needs about saving is derived from this, rather than tracked separately.
   */
  readonly queueSize = this.pendingCount.asReadonly();

  /**
   * True while anything is queued - waiting or in flight. Everything on the queue is a write, so
   * this is what the UI gates on: reads and lifecycle actions are no longer queued, they are
   * blocked from starting while a save is still outstanding. Exposed to components as
   * TaAppStore.isSavingData, and rendered as the "Saving..." indicator in bread-crumb-bar.
   */
  readonly isSaving = computed(() => this.queueSize() > 0);

  constructor() {
    this.queue$
      .pipe(
        concatMap(queued =>
          // defer, so a task factory that throws synchronously - dereferencing a TA that has since
          // been reset, say - becomes an error notification here instead of killing this outer
          // subscription. Without it the queue is left with no subscriber at all: every later
          // enqueue is counted but never runs, isSaving() sticks true, and saves stop silently.
          defer(() => queued.task()).pipe(
            tap(result => {
              queued.subject.next(result);
              queued.subject.complete();
            }),
            catchError(err => {
              queued.subject.error(err);
              return of(null);
            }),
            // After catchError so it runs exactly once per task, on success or failure - a task
            // left counted would wedge every button gated on isSaving().
            finalize(() => this.pendingCount.update(count => count - 1))
          )
        )
      )
      .subscribe();
  }

  enqueue<T = any>(task: QueueTask<T>): Observable<T> {
    const subject = new Subject<T>();
    // Counted on enqueue, not when concatMap reaches it: a task still waiting its turn is an
    // unsaved change too.
    this.pendingCount.update(count => count + 1);
    this.queue$.next({ task, subject });
    return subject.asObservable();
  }
}
